/**
 * Generic sticky add-to-cart bar.
 *
 * Auto-detects the page context:
 *   - Bundle PDP  -> forwards click to `.bundle-add-to-cart`
 *                    (the bundle JS owns that handler)
 *   - Standard PDP -> forwards click to the main
 *                     `.product-single__form .add-to-cart`
 *                     which submits the form (also fires AJAX
 *                     handlers themes attach to that button).
 *
 * Visibility: scroll/resize watcher on the main ATC's container that
 *             also accounts for the sticky site header. The bar
 *             appears once the sentinel either scrolls off the top of
 *             the viewport OR is hidden under the pinned header.
 * Price sync: MutationObserver on `.product__price`. On bundle PDPs we
 *             also observe `[data-bundle-total]` and prefer its value
 *             when it is visible (so the sticky bar reflects the
 *             current bundle total without mutating `.product__price`).
 * Disabled sync: MutationObserver on the main ATC button.
 */
(function () {
  'use strict';

  function init() {
    var stickyBar = document.querySelector('[data-sticky-add-to-cart]');
    if (!stickyBar) return;

    // Find the main ATC button (bundle PDPs win first).
    var mainButton =
      document.querySelector('.bundle-add-to-cart') ||
      document.querySelector('.product-single__form .add-to-cart') ||
      document.querySelector('.product-single__form [name="add"]');

    if (!mainButton) return;

    // The sentinel we watch for visibility. On bundle PDPs the bundle
    // ATC button is relocated next to the price/app block at the top
    // of the meta column (see subscription-bundle.js), so we prefer
    // that wrapper — the sticky bar should appear as soon as that row
    // is no longer reachable, not when the bundle's bottom action area
    // leaves the viewport.
    var sentinel =
      document.querySelector('.bundle-atc-row') ||
      document.querySelector('.bundle-actions') ||
      mainButton.closest('.product-single__form') ||
      mainButton;

    var stickyButton = stickyBar.querySelector('[data-sticky-add-to-cart-button]');
    var stickyPrice = stickyBar.querySelector('[data-sticky-add-to-cart-price]');
    var pageMainPrice = document.querySelector('.product__price');
    var bundleTotalWrap = document.querySelector('[data-bundle-total]');
    var bundleTotalPrice = document.querySelector('[data-bundle-total-price]');

    // ---- Cache original price text so we can restore it if the page
    //      ever resets to "no selection" state.
    if (stickyPrice) {
      stickyPrice.dataset.originalPrice = stickyPrice.textContent.trim();
    }

    // ---- Mirror the active price into the sticky bar --------------
    //
    // Prefer the bundle total when it is visible (bundle PDPs, after
    // the user has picked at least one item). Otherwise fall back to
    // the main `.product__price`. This keeps the sticky bar in sync
    // with the bundle without ever mutating `.product__price`.
    function getActivePriceText() {
      if (bundleTotalWrap && !bundleTotalWrap.hidden && bundleTotalPrice) {
        var bundleText = bundleTotalPrice.textContent.trim();
        if (bundleText) return bundleText;
      }
      if (pageMainPrice) {
        return pageMainPrice.textContent.trim();
      }
      return '';
    }

    function syncStickyPrice() {
      if (!stickyPrice) return;
      var next = getActivePriceText();
      if (next) stickyPrice.textContent = next;
    }

    if (stickyPrice && (pageMainPrice || bundleTotalWrap)) {
      // Initial sync (in case bundle/theme code already updated the
      // price before this script ran).
      syncStickyPrice();

      if (pageMainPrice) {
        var mainPriceObserver = new MutationObserver(syncStickyPrice);
        mainPriceObserver.observe(pageMainPrice, {
          characterData: true,
          childList: true,
          subtree: true
        });
      }

      if (bundleTotalWrap) {
        var bundleTotalObserver = new MutationObserver(syncStickyPrice);
        bundleTotalObserver.observe(bundleTotalWrap, {
          characterData: true,
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['hidden']
        });
      }
    }

    // ---- Mirror the main button's disabled + loading state --------
    //
    // The main ATC button gets `.btn--loading` added by the theme
    // (theme.js / subscription-bundle.js) while the AJAX add-to-cart
    // request is in flight, and removed once it resolves. We mirror
    // that class onto the sticky button so the spinner stays visible
    // there too — and so it's automatically cleared once the cart
    // drawer opens, without the sticky bar needing to know anything
    // about the request lifecycle.
    function syncState() {
      if (!stickyButton) return;
      var disabled = mainButton.disabled || mainButton.classList.contains('is-disabled');
      var loading = mainButton.classList.contains('btn--loading');
      // While loading, keep the sticky button non-interactive but
      // don't show the dimmed `is-disabled` style on top of the
      // spinner — `btn--loading` already handles its own visuals.
      stickyButton.disabled = disabled || loading;
      stickyButton.classList.toggle('is-disabled', disabled && !loading);
      stickyButton.classList.toggle('btn--loading', loading);
    }
    syncState();

    var stateObserver = new MutationObserver(syncState);
    stateObserver.observe(mainButton, {
      attributes: true,
      attributeFilter: ['disabled', 'class']
    });

    // ---- Forward sticky button clicks to the main button ----------
    if (stickyButton) {
      stickyButton.addEventListener('click', function (e) {
        e.preventDefault();
        if (stickyButton.disabled || stickyButton.classList.contains('btn--loading')) return;

        // Immediate visual feedback on the sticky button itself, so
        // the user sees the spinner the moment they tap — even
        // before the main button picks up its own loading class
        // (which then keeps the state in sync via syncState above).
        stickyButton.classList.add('btn--loading');
        stickyButton.disabled = true;

        // .click() preserves the main button's behavior:
        //   - bundle JS click handler (opens cart drawer on success)
        //   - standard product form submit (theme AJAX handler opens
        //     cart drawer via `ajaxProduct:added`)
        mainButton.click();
      });
    }

    // ---- Show/hide the sticky bar ---------------------------------
    //
    // The sticky bar is visible once the sentinel has scrolled past
    // the top of the visible area. "Top of the visible area" is the
    // viewport top by default, OR the bottom edge of the pinned site
    // header (when the theme has applied `.site-header--stuck`) — that
    // way the bar also appears when the sentinel slides under the
    // sticky header and is therefore no longer reachable, not just
    // when it leaves the viewport entirely.
    stickyBar.classList.add('is-active'); // takes it out of display:none

    // Resolve the actual element that pins to the top of the viewport
    // when scrolled. Theme uses `.site-header` (gets `.site-header--stuck`
    // and `position: fixed`); fall back to `#StickyHeaderWrap` /
    // `#HeaderWrapper` so this also works on themes that pin the
    // wrapper instead.
    var siteHeader =
      document.querySelector('.site-header') ||
      document.getElementById('StickyHeaderWrap') ||
      document.getElementById('HeaderWrapper');

    function getOcclusionTop() {
      if (!siteHeader) return 0;

      // Only count the header as occluding content when it's actually
      // pinned to the viewport — i.e. its computed position is
      // `fixed` or `sticky`. While the header sits in normal flow it
      // scrolls with the page and overlaps nothing below it.
      var position = window.getComputedStyle(siteHeader).position;
      if (position !== 'fixed' && position !== 'sticky') return 0;

      var rect = siteHeader.getBoundingClientRect();

      // During the theme's slide-in transition the header is
      // translated -100% (above the viewport) before it animates
      // back to 0. Treat anything that hasn't actually rendered
      // into the viewport as "not covering anything yet".
      if (rect.bottom <= 0) return 0;

      return rect.bottom;
    }

    function updateVisibility() {
      var sentinelRect = sentinel.getBoundingClientRect();
      var occlusionTop = getOcclusionTop();
      // Show the sticky bar the moment ANY part of the sentinel is
      // covered — i.e. as soon as the sentinel's top crosses below
      // the occlusion line (sticky header bottom, or viewport top
      // when no header is pinned). This matches the natural "the
      // ATC just disappeared from view" feel.
      var hidden = sentinelRect.top < occlusionTop;
      stickyBar.classList.toggle('is-visible', hidden);
      stickyBar.setAttribute('aria-hidden', hidden ? 'false' : 'true');
    }

    var rafPending = false;
    function scheduleUpdate() {
      if (rafPending) return;
      rafPending = true;
      window.requestAnimationFrame(function () {
        rafPending = false;
        updateVisibility();
      });
    }

    // Listen on both window and document — some legacy themes /
    // smooth-scroll wrappers stop scroll events at one or the other.
    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    document.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate, { passive: true });

    // The header pin/unpin and the slide-in animation both happen via
    // class changes on `.site-header` (`.site-header--stuck` →
    // `.site-header--opening`). A MutationObserver picks both up so
    // the bar reacts to the pinned header the instant it lands, even
    // without a fresh scroll event.
    if (siteHeader) {
      var headerStateObserver = new MutationObserver(scheduleUpdate);
      headerStateObserver.observe(siteHeader, {
        attributes: true,
        attributeFilter: ['class', 'style']
      });

      // The `.site-header--opening` class is the one that slides the
      // pinned header back into view via a 0.4s CSS transform. Hook
      // the transitionend so the bar recomputes once the header has
      // actually settled into its final on-screen position.
      siteHeader.addEventListener('transitionend', function (e) {
        if (e.propertyName === 'transform') scheduleUpdate();
      });
    }

    updateVisibility();
  }

  // We rely on DOM nodes that other scripts inject during their own
  // `DOMContentLoaded` handlers (notably subscription-bundle.js, which
  // creates `.bundle-atc-row`). This file is loaded with `defer`, so by
  // the time it executes `readyState` is already `'interactive'` —
  // meaning the original `'loading'`-only check would call `init()`
  // immediately, BEFORE `DOMContentLoaded` fires and BEFORE those
  // sibling scripts have inserted their nodes. Wait until DCL has
  // actually fired so all PDP scripts have finished setting up.
  if (document.readyState === 'complete') {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }
})();
